import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';

import {
  CustomPopupConfig,
  DEFAULT_QUIZ_MODES,
  DontShowAgainPolicy,
  MaestroReminderPreview,
  QUIZ_MODES,
  QuizMode,
} from '../../../../devtools/screens/Popups/CustomPopups';
import {
  CREATE_CUSTOM_POPUP,
  DELETE_CUSTOM_POPUP,
  GET_CUSTOM_POPUPS,
  UPDATE_CUSTOM_POPUP,
} from '../../../../devtools/screens/Popups/graphql';

// Same values as the server's DEFAULT_MAESTRO_REMINDER_CONFIG (customPopupConfig/store.ts): the
// reminder the game gets while none is saved here.
const DEFAULT_ID = 'maestro-reminder-default';
const DEFAULTS = {
  reshow: true,
  reshowAfterQuizzes: '3',
  reshowQuizModes: DEFAULT_QUIZ_MODES,
  dailyCap: '10',
  dontShowAgainPolicy: 'SESSION' as DontShowAgainPolicy,
  showSecondaryButton: true,
  targetUserIds: '',
};

const POLICIES: { value: DontShowAgainPolicy; label: string; checkbox: string }[] = [
  { value: 'SESSION', label: 'Rest of the session', checkbox: "Don't show again this session" },
  { value: 'DAY', label: 'Rest of the day', checkbox: "Don't show again today" },
  { value: 'FOREVER', label: 'Forever', checkbox: "Don't show again" },
  { value: 'NONE', label: 'None (hide the checkbox)', checkbox: '' },
];

type FormState = typeof DEFAULTS;

const toFormState = (config: CustomPopupConfig | undefined): FormState =>
  config
    ? {
        reshow: config.reshowAfterQuizzes != null,
        reshowAfterQuizzes: String(config.reshowAfterQuizzes ?? DEFAULTS.reshowAfterQuizzes),
        reshowQuizModes: config.reshowQuizModes?.length ? config.reshowQuizModes : DEFAULT_QUIZ_MODES,
        dailyCap: String(config.dailyCap),
        dontShowAgainPolicy: config.dontShowAgainPolicy ?? 'SESSION',
        showSecondaryButton: config.showSecondaryButton !== false,
        targetUserIds: (config.targetUserIds ?? []).join('\n'),
      }
    : DEFAULTS;

/**
 * Songpop Maestro reminder popup (main menu, MAESTRO_REMINDER template). On by default: while
 * nothing is saved here, the server sends its default config. Who sees it and when follow the
 * Maestro event (only players with an event on sale for them, never an owner), and the texts are
 * the game's own, translated with the app. Only how often it shows is set here.
 */
export function MaestroReminderSettings() {
  const { data, loading, refetch } = useQuery<{ liveOps: { customPopupConfigs: CustomPopupConfig[] } }>(
    GET_CUSTOM_POPUPS,
    { fetchPolicy: 'network-only' }
  );
  const [createPopup, { loading: creating }] = useMutation(CREATE_CUSTOM_POPUP);
  const [updatePopup, { loading: updating }] = useMutation(UPDATE_CUSTOM_POPUP);
  const [deletePopup, { loading: deleting }] = useMutation(DELETE_CUSTOM_POPUP);

  const saved = (data?.liveOps.customPopupConfigs ?? []).filter((config) => config.template === 'MAESTRO_REMINDER');
  const config = saved[0];
  const extras = saved.slice(1);

  const [form, setForm] = useState<FormState>(DEFAULTS);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    const next = toFormState(config);
    setForm(next);
    setShowAdvanced(!!next.targetUserIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.id, data]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setMessage(null);
    setForm((current) => ({ ...current, [key]: value }));
  };

  const toggleQuizMode = (mode: QuizMode, checked: boolean) =>
    set(
      'reshowQuizModes',
      checked ? [...form.reshowQuizModes, mode] : form.reshowQuizModes.filter((value) => value !== mode)
    );

  const buildInput = (enabled: boolean) => {
    const dailyCap = Number(form.dailyCap);
    if (!Number.isInteger(dailyCap) || dailyCap < 1) {
      throw new Error('Daily cap must be a whole number of at least 1.');
    }
    const reshowText = form.reshowAfterQuizzes.trim();
    const reshowAfterQuizzes = form.reshow ? Number(reshowText) : null;
    if (
      reshowAfterQuizzes != null &&
      (!Number.isInteger(reshowAfterQuizzes) || reshowAfterQuizzes < 1 || reshowAfterQuizzes > 100)
    ) {
      throw new Error('Show again after must be from 1 to 100 quizzes.');
    }
    if (reshowAfterQuizzes != null && !form.reshowQuizModes.length) {
      throw new Error('Pick at least one quiz mode to count.');
    }
    return {
      name: 'Songpop Maestro reminder',
      enabled,
      place: 'MAIN_MENU',
      // The game's own texts and art: nothing to author.
      titleTexts: {},
      bodyTexts: {},
      buttonTexts: {},
      buttonAction: 'DISMISS',
      buttonUrl: null,
      isSurveyLink: false,
      backgroundImageUrl: null,
      secondaryButtonTexts: null,
      showSecondaryButton: form.showSecondaryButton,
      dontShowAgainTexts: null,
      dontShowAgainPolicy: form.dontShowAgainPolicy,
      dailyCap,
      // Audience and schedule follow the Maestro event: only QA target users can be set here.
      targetUserIds: form.targetUserIds
        .split(/[\s,]+/)
        .map((id) => id.trim())
        .filter(Boolean),
      platform: null,
      minAppVersion: null,
      maxAppVersion: null,
      versionConditions: null,
      countries: [],
      languages: [],
      minLevel: null,
      maxLevel: null,
      levelConditions: null,
      payerStatus: 'ALL',
      vipStatus: 'ALL',
      startDate: null,
      endDate: null,
      template: 'MAESTRO_REMINDER',
      reshowAfterQuizzes,
      reshowQuizModes: reshowAfterQuizzes != null ? form.reshowQuizModes : null,
    };
  };

  const onSave = async () => {
    setError(null);
    setMessage(null);
    try {
      const input = buildInput(config?.enabled ?? true);
      if (config) {
        await updatePopup({ variables: { input: { id: config.id, ...input } } });
      } else {
        await createPopup({ variables: { input: { id: DEFAULT_ID, ...input } } });
      }
      await refetch();
      setMessage('Saved. It can take a few minutes to show in the app.');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : String(saveError));
    }
  };

  // The on/off switch, saved on its own: off keeps the other settings for when it is turned back on.
  const onToggle = async (enabled: boolean) => {
    setError(null);
    setMessage(null);
    try {
      if (config) {
        await updatePopup({ variables: { input: { id: config.id, enabled } } });
      } else {
        await createPopup({ variables: { input: { id: DEFAULT_ID, ...buildInput(enabled) } } });
      }
      await refetch();
    } catch (toggleError) {
      setError(toggleError instanceof Error ? toggleError.message : String(toggleError));
    }
  };

  const onDelete = async (ids: string[]) => {
    setError(null);
    setMessage(null);
    try {
      for (const id of ids) {
        await deletePopup({ variables: { id } });
      }
      await refetch();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : String(deleteError));
    }
  };

  const saving = creating || updating || deleting;
  const checkboxLabel = POLICIES.find((policy) => policy.value === form.dontShowAgainPolicy)?.checkbox ?? '';
  const status = !config ? (
    <Badge variant="success">ON (default)</Badge>
  ) : config.enabled ? (
    <Badge variant="success">ON</Badge>
  ) : (
    <Badge variant="secondary">OFF</Badge>
  );

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex align-items-center">
        <strong className="mr-3">Reminder popup</strong>
        {loading ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <>
            <Form.Check
              type="switch"
              id="maestro-reminder-enabled"
              label=""
              checked={config ? config.enabled : true}
              disabled={saving}
              onChange={(event) => setPendingEnabled(event.currentTarget.checked)}
            />
            {status}
          </>
        )}
      </Card.Header>
      <Card.Body>
        <p className="text-muted">Shows in the main menu, after the other home popups.</p>

        {extras.length > 0 && (
          <Alert variant="warning">
            {saved.length} reminder popups are saved and the game shows each of them. Only the first one ({config?.name}
            ) is edited here.{' '}
            <Button
              size="sm"
              variant="outline-danger"
              disabled={saving}
              onClick={() => onDelete(extras.map((extra) => extra.id))}
            >
              Delete the other {extras.length}
            </Button>
          </Alert>
        )}

        <Row>
          <Col lg={7}>
            {/* One setting: whether it comes back in the session, after how many quizzes and of which modes */}
            <div className="border rounded p-2 mb-3">
              <Form.Check
                type="switch"
                id="maestro-reminder-reshow"
                label="Show again in the same session"
                checked={form.reshow}
                onChange={(event) => set('reshow', event.currentTarget.checked)}
              />
              {form.reshow ? (
                <>
                  <div className="d-flex align-items-center mt-2">
                    <span className="mr-2">after</span>
                    <Form.Control
                      size="sm"
                      style={{ width: 70 }}
                      value={form.reshowAfterQuizzes}
                      onChange={(event) => set('reshowAfterQuizzes', event.target.value)}
                    />
                    <span className="ml-2">quizzes of:</span>
                  </div>
                  <div className="d-flex flex-wrap mt-2">
                    {QUIZ_MODES.map((mode) => (
                      <Form.Check
                        key={mode.value}
                        type="checkbox"
                        id={`maestro-reminder-quiz-mode-${mode.value}`}
                        className="mr-3"
                        style={{ minWidth: '45%' }}
                        checked={form.reshowQuizModes.includes(mode.value)}
                        onChange={(event) => toggleQuizMode(mode.value, event.currentTarget.checked)}
                        label={mode.label}
                      />
                    ))}
                  </div>
                  <Form.Text muted>
                    Only when the player closed it without ticking &quot;Don&apos;t show again&quot;.
                  </Form.Text>
                </>
              ) : (
                <Form.Text muted>Off: once per session.</Form.Text>
              )}
            </div>

            <Form.Group>
              <Form.Label>Daily cap (shows/day)</Form.Label>
              <Form.Control
                style={{ maxWidth: 120 }}
                value={form.dailyCap}
                onChange={(event) => set('dailyCap', event.target.value)}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>&quot;Don&apos;t show again&quot; hides it for</Form.Label>
                  <Form.Control
                    as="select"
                    value={form.dontShowAgainPolicy}
                    onChange={(event) => set('dontShowAgainPolicy', event.currentTarget.value as DontShowAgainPolicy)}
                  >
                    {POLICIES.map((policy) => (
                      <option key={policy.value} value={policy.value}>
                        {policy.label}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>&nbsp;</Form.Label>
                  <Form.Check
                    type="switch"
                    id="maestro-reminder-later"
                    label='Show the "LATER" button'
                    checked={form.showSecondaryButton}
                    onChange={(event) => set('showSecondaryButton', event.currentTarget.checked)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Button variant="link" className="px-0" onClick={() => setShowAdvanced(!showAdvanced)}>
              {showAdvanced ? '▾' : '▸'} Advanced
            </Button>
            {showAdvanced && (
              <Form.Group>
                <Form.Label>Target user IDs (one per line, empty = every player of the event)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={form.targetUserIds}
                  onChange={(event) => set('targetUserIds', event.target.value)}
                />
                <Form.Text muted>
                  For testing: only these players get it, still only while the event is on sale for them.
                </Form.Text>
              </Form.Group>
            )}

            {error && <Alert variant="danger">{error}</Alert>}
            {message && <Alert variant="success">{message}</Alert>}

            <div className="d-flex">
              <Button variant="primary" disabled={saving || loading} onClick={onSave}>
                {saving ? <Spinner animation="border" size="sm" /> : 'Save'}
              </Button>
              {config && (
                <Button
                  variant="outline-secondary"
                  className="ml-2"
                  disabled={saving}
                  onClick={() => setConfirmReset(true)}
                >
                  Back to the defaults
                </Button>
              )}
            </div>
          </Col>
          <Col lg={5}>
            <MaestroReminderPreview
              titleJson="{}"
              buttonJson="{}"
              secondaryJson="{}"
              showSecondaryButton={form.showSecondaryButton}
              dontShowAgainJson={JSON.stringify({ en: checkboxLabel })}
              dontShowAgainPolicy={form.dontShowAgainPolicy}
            />
          </Col>
        </Row>
      </Card.Body>

      <Modal show={pendingEnabled !== null} onHide={() => setPendingEnabled(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{pendingEnabled ? 'Turn the reminder on?' : 'Turn the reminder off?'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingEnabled
            ? 'Players the Maestro event is on sale for get the reminder again in the main menu.'
            : 'No player gets the reminder any more, even while an event is on sale. The settings are kept.'}{' '}
          It can take a few minutes to show in the app.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPendingEnabled(null)}>
            Cancel
          </Button>
          <Button
            variant={pendingEnabled ? 'success' : 'danger'}
            onClick={() => {
              const enabled = !!pendingEnabled;
              setPendingEnabled(null);
              onToggle(enabled);
            }}
          >
            {pendingEnabled ? 'Turn on' : 'Turn off'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={confirmReset} onHide={() => setConfirmReset(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Back to the defaults?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          The saved settings are deleted and the reminder goes back to the default ones: on, again after 3 quizzes, at
          most 10 times a day.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmReset(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              setConfirmReset(false);
              onDelete(saved.map((popup) => popup.id));
            }}
          >
            Back to the defaults
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}
